import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import '../features/persons/pessoas-2b.css';
import { UserAvatar } from '../components/UserAvatar';
import { maskCpf, riskBand } from '../features/persons/persons-adapter';
import { usePersons } from '../features/persons/use-persons';

const PAGE_SIZE = 30;

/**
 * PessoasPage — rota `/pessoas` (Brief 2B T2, landing — D4).
 *
 * Busca/lista de pessoas (`GET /v1/persons?q=`) como entrada da Tela 2. O card
 * navega para o detalhe consolidado `/pessoas/:personId`. Read-only.
 */
export function PessoasPage(): JSX.Element {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Debounce simples da busca (300ms). Busca nova reinicia o tamanho da página.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(input.trim());
      setLimit(PAGE_SIZE);
    }, 300);
    return () => {
      clearTimeout(t);
    };
  }, [input]);

  const { data, isLoading, isError } = usePersons(q !== '' ? { q, limit } : { limit });
  const persons = data?.data ?? [];

  return (
    <div className="flex flex-col h-full">
      <div className="app-pagehead">
        <div className="app-pagehead__top">
          <h1 className="app-pagehead__title">Pessoas</h1>
          <span className="app-pagehead__sub">
            {persons.length}
            {data?.meta.hasMore === true ? '+' : ''} {persons.length === 1 ? 'pessoa' : 'pessoas'}
          </span>
        </div>
      </div>

      <div className="person-list-page">
        <div className="person-search">
          <Search size={15} aria-hidden style={{ color: 'var(--fg-3)' }} />
          <input
            type="search"
            placeholder="Buscar por nome, CPF, e-mail ou telefone…"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
            }}
            aria-label="Buscar pessoas"
          />
        </div>

        {isLoading ? (
          <div className="person-empty">Carregando pessoas…</div>
        ) : isError ? (
          <div className="person-empty">Não foi possível carregar as pessoas.</div>
        ) : persons.length === 0 ? (
          <div className="person-empty">
            {q !== '' ? `Nenhuma pessoa encontrada para “${q}”.` : 'Nenhuma pessoa cadastrada.'}
          </div>
        ) : (
          <ul className="person-list">
            {persons.map((p) => {
              const band = riskBand(p.riskScore);
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    className="person-list-card"
                    onClick={() => navigate(`/pessoas/${p.id}`)}
                  >
                    <UserAvatar name={p.displayName} colorKey={p.id} size="md" />
                    <div className="person-list-card__body">
                      <span className="person-list-card__name">{p.displayName}</span>
                      <span className="person-list-card__sub">
                        {p.primaryIdentifierValue !== null
                          ? p.primaryIdentifierType === 'cpf'
                            ? maskCpf(p.primaryIdentifierValue)
                            : p.primaryIdentifierValue
                          : 'sem identificador'}{' '}
                        · {p.identityCount} {p.identityCount === 1 ? 'canal' : 'canais'} ·{' '}
                        {p.bondCount} {p.bondCount === 1 ? 'vínculo' : 'vínculos'}
                      </span>
                    </div>
                    <span className="person-list-card__meta">{band.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {!isLoading && !isError && data?.meta.hasMore === true && (
          <div style={{ textAlign: 'center', padding: 'var(--space-3)' }}>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setLimit((l) => l + PAGE_SIZE)}
            >
              Carregar mais
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
